// src/forgot/ForgotRequest.jsx
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AuthShell, { AUTH_BRAND_BLUE as BLUE } from "../auth/AuthShell.jsx";
import {
  fadeUp,
  hoverRaise,
  staggerContainer,
} from "../Animations/motionpresets";
import EmailIcon from "../Icons/EmailIcon.jsx";

const brandCircleStyle = {
  background: "linear-gradient(135deg,#2A7EFA,#5AA9FF)",
};

// Build the correct URL for the towing portal reset page
const ORIGIN =
  (import.meta?.env?.VITE_FRONTEND_ORIGIN || "").replace(/\/$/, "") ||
  window.location.origin;
const FRONTEND_URL = `${ORIGIN}/towing/reset-password`; // <-- define it here

async function requestPasswordReset(email) {
  const res = await fetch(
    "https://towing-portal-api.onrender.com/api/v1/auth/password-reset/request",
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: String(email || "")
          .trim()
          .toLowerCase(),
        frontend_url: FRONTEND_URL, // <-- use the defined constant
      }),
    }
  );
  const txt = await res.text();
  let data = null;
  try {
    data = txt ? JSON.parse(txt) : null;
  } catch {}
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || txt || `Request failed (${res.status})`
    );
  return data || { message: "Password reset email sent successfully." };
}

export default function ForgotRequest() {
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    const v = email.trim();
    if (!v || !v.includes("@")) {
      setErr("Enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const resp = await requestPasswordReset(v);
      localStorage.setItem("last_reset_email", v.toLowerCase());
      setSuccessMsg(resp?.message || "Password reset email sent successfully.");
    } catch (er) {
      setErr(er.message || "Could not send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <motion.div
        key={location.key}
        variants={staggerContainer(0.12, 0.18)}
        initial="hidden"
        animate="show"
      >
        <motion.div className="mb-4 flex items-center gap-2" variants={fadeUp}>
          <span
            className="inline-grid place-items-center h-8 w-8 rounded-full text-white ring-1 ring-white/50"
            style={brandCircleStyle}
          >
            <EmailIcon className="w-5 h-5" />
          </span>
          <span className="text-sm font-semibold text-slate-900">
            Towing Portal
          </span>
        </motion.div>

        <motion.div className="text-left" variants={fadeUp}>
          <div className="mb-1 text-[15px] font-bold text-black">
            Reset password
          </div>
          <h1
            className="text-2xl font-extrabold leading-tight"
            style={{ color: BLUE }}
          >
            Forgot your password?
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Enter your email and we’ll send you a secure reset link.
          </p>
        </motion.div>

        <AnimatePresence initial={false} mode="wait">
          {successMsg ? (
            <motion.div
              key="success"
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/90 p-4 text-emerald-800"
            >
              <div className="font-semibold mb-1">{successMsg}</div>
              <p className="text-sm">
                Please check your inbox. If it’s not there, look in your{" "}
                <span className="font-medium">Spam</span> or{" "}
                <span className="font-medium">Junk</span> folder.
              </p>
              <div className="mt-4 pt-3 border-t border-emerald-200/60 text-[11px] text-emerald-700/90">
                Tip: the link may expire. Use it soon.
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={onSubmit}
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-5"
            >
              <motion.label
                className="block text-sm text-slate-700"
                variants={fadeUp}
              >
                <span className="font-bold">Email</span>
                <motion.div className="relative mt-2 group" {...hoverRaise}>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-2
                               text-slate-900 placeholder-slate-400 outline-none
                               focus:border-sky-400 focus:ring-2 focus:ring-sky-200/60 disabled:opacity-70"
                  />
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500">
                    <EmailIcon className="w-5 h-5" />
                  </span>
                </motion.div>
              </motion.label>

              <AnimatePresence>
                {err && (
                  <motion.div
                    key="err"
                    initial={{ y: -6, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -6, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 26 }}
                    className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm"
                  >
                    {err}
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.button
                type="submit"
                disabled={loading}
                variants={fadeUp}
                whileHover={{ y: -1, scale: 1.01 }}
                whileTap={{ y: 0, scale: 0.995 }}
                style={{ background: BLUE, boxShadow: `0 12px 28px ${BLUE}33` }}
                className="mt-4 w-full rounded-xl py-2.5 font-semibold text-white cursor-pointer transition
                           hover:brightness-[1.04] active:translate-y-px disabled:opacity-70"
              >
                {loading ? "Sending…" : "Send reset link"}
              </motion.button>

              <motion.div
                variants={fadeUp}
                className="mt-4 text-center text-xs"
              >
                <button
                  type="button"
                  onClick={() => nav("/towing/login")} // <-- towing login route
                  className="text-sky-600 hover:underline"
                >
                  Back to login
                </button>
              </motion.div>
            </motion.form>
          )}
        </AnimatePresence>

        <motion.div
          variants={fadeUp}
          className="mt-5 pt-3 border-t border-slate-200 text-[11px] text-slate-500 text-left"
        >
          We’ll never email you links asking for your password.
        </motion.div>
      </motion.div>
    </AuthShell>
  );
}
